import React, { useEffect, useState } from 'react';
import authAxios from '../utils/authAxios';
import StandardTableLayout from '../styles/StandardTableLayout';
import {
  Input,
  Button,
  DangerButton,
  SecondaryButton
} from '../styles/TableStyles';

import { mapColumnLabels } from '../styles/ColumnUtils';
import { savedToast, errorToast, deletedToast } from '../utils/toastUtils';

const initialFormState = {
  Name: '',
  Charge: '',
};

const visibleColumns = mapColumnLabels(Object.keys(initialFormState)).map(col =>
  col.key === 'Charge' ? { ...col, format: 'currency' } : col
);

const SetupChargesPage = () => {
  const [loading, setLoading] = useState(true);
  const [setupCharges, setSetupCharges] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchSetupCharges();
  }, []);

  const fetchSetupCharges = async () => {
    setLoading(true);
    try {
      const res = await authAxios.get('/api/setupcharges');
      setSetupCharges(res.data);
    } catch (err) {
      console.error('Error fetching SetupCharges:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const cleanedForm = {};
      for (let key in formData) {
        let val = formData[key]?.toString().trim();
        if (val === '') {
          cleanedForm[key] = null;
        } else if (key === 'Charge') {
          cleanedForm[key] = parseFloat(val);
        } else {
          cleanedForm[key] = val;
        }
      }

      if (isEditing) {
        await authAxios.put(`/api/setupcharges/${editingId}`, cleanedForm);
      } else {
        await authAxios.post('/api/setupcharges', cleanedForm);
      }

      setFormData(initialFormState);
      setShowForm(false);
      setIsEditing(false);
      setEditingId(null);
      fetchSetupCharges();
      savedToast('Setup Charge');
    } catch (err) {
      console.error('Error submitting SetupCharge:', err);
      errorToast();
    }
  };

  const handleEdit = (row) => {
    setFormData({ Name: row.Name, Charge: row.Charge });
    setShowForm(false);
    setIsEditing(true);
    setEditingId(row.UniqueID);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    try {
      await authAxios.delete(`/api/setupcharges/${id}`);
      fetchSetupCharges();
      deletedToast('Setup Charge');
    } catch (err) {
      console.error('Error deleting SetupCharge:', err);
    }
  };

  const filteredData = setupCharges.filter(entry =>
    Object.values(entry).some(val =>
      String(val).toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  return loading ? (
    <div style={{ padding: '2rem' }}>Loading Setup Charges...</div>
  ) : (
    <StandardTableLayout
      title="Setup Charges"
      showForm={showForm}
      onToggleForm={() => {
        setShowForm(!showForm);
        setIsEditing(false);
        setFormData(initialFormState);
      }}
      onSubmit={handleSubmit}
      columns={visibleColumns}
      editingId={editingId}
      formData={formData}
      handleChange={handleChange}
      rows={filteredData}
      searchValue={searchQuery}
      onSearchChange={(e) => setSearchQuery(e.target.value)}
      renderCell={(col, data, handleChange) => {
        if (!handleChange) {
          return (
            <span style={{ background: 'transparent', display: 'inline-block', padding: '4px 0' }}>
              {String(data?.[col] ?? '')}
            </span>
          );
        }
        return (
          <Input
            type="text"
            name={col}
            value={data[col]}
            onChange={handleChange}
          />
        );
      }}
      renderActions={(row) => (
        editingId === row.UniqueID ? (
          <>
            <Button type="submit">Save</Button>
            <SecondaryButton
              type="button"
              onClick={() => {
                setIsEditing(false);
                setEditingId(null);
                setFormData(initialFormState);
              }}
            >
              Cancel
            </SecondaryButton>
          </>
        ) : (
          <>
            <SecondaryButton type="button" onClick={() => handleEdit(row)}>Edit</SecondaryButton>
            <DangerButton type="button" onClick={() => handleDelete(row.UniqueID)}>Delete</DangerButton>
          </>
        )
      )}
    />
  );
};

export default SetupChargesPage;